'use client';

import React, { useEffect, useState } from 'react';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './RangeSlider.scss';
import { HandleProps } from 'rc-slider/es/Handles/Handle';
import { Dictionary } from '@/src/lib/dictionary/types';
import InputField from '@/src/app/components/InputField/InputField';
export type RangeSliderProps = {
  dict: Dictionary;
  min?: number;
  max?: number;
  activeMin?: number;
  activeMax?: number;
  step?: number;
  handleChange?: CallableFunction;
  disabled?: boolean;
  className?: string;
};
export default function RangeSlider({
  min = 0,
  max = 100,
  activeMin = min,
  activeMax = max,
  step = max / 10,
  handleChange,
  dict,
  disabled,
  className
}: RangeSliderProps) {
  const [currentMin, setCurrentMin] = useState<number>(activeMin);
  const [currentMax, setCurrentMax] = useState<number>(activeMax);
  useEffect(() => {
    updateMinValue(min);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [min]);
  useEffect(() => {
    updateMaxValue(max);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [max]);
  useEffect(() => {
    if (activeMin >= min) {
      updateMinValue(activeMin);
    }
    if (activeMax <= max) {
      updateMaxValue(activeMax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  function handleRender(renderProps: React.ReactElement<HandleProps>) {
    const allRenderProps = renderProps as unknown as HandleProps & {
      props: {
        ['aria-valuenow']: string;
      };
    };
    const currentValue = allRenderProps.props['aria-valuenow'];
    return (
      // @ts-ignore
      <div {...renderProps.props}>
        <span className="hidden absolute top-full text-xs mt-3 font-semibold">{currentValue}</span>
      </div>
    );
  }
  function onChangeSlider(value: number | Array<number>) {
    if (!Array.isArray(value)) return;
    const [newMin, newMax] = value;
    setCurrentMin(newMin);
    setCurrentMax(newMax);
  }
  function onKeyUpInput(event: React.KeyboardEvent<HTMLInputElement>) {
    const allowedKeys = ['Enter', 'ArrowUp', 'ArrowDown'];
    if (!allowedKeys.includes(event.key)) return;
    const {
      value,
      name
    } = event.target as HTMLInputElement;
    if (name === 'min') {
      updateMinValue(+value);
      if (handleChange) {
        handleChange([determineMin(+value), currentMax]);
      }
    }
    if (name === 'max') {
      updateMaxValue(+value);
      if (handleChange) {
        handleChange([currentMin, determineMax(+value)]);
      }
    }
  }
  function onBlurInput(event: React.FocusEvent<HTMLInputElement>) {
    const {
      value,
      name
    } = event.target as HTMLInputElement;
    if (name === 'min') {
      updateMinValue(+value);
      if (handleChange) {
        handleChange([determineMin(+value), currentMax]);
      }
    }
    if (name === 'max') {
      updateMaxValue(+value);
      if (handleChange) {
        handleChange([currentMin, determineMax(+value)]);
      }
    }
  }
  function determineMin(value: number) {
    if (value > currentMax) {
      return currentMax;
    }
    if (value < min) {
      return min;
    }
    return value;
  }
  function determineMax(value: number) {
    if (value < currentMin) {
      return currentMin;
    }
    if (value > max) {
      return max;
    }
    return value;
  }
  function updateMinValue(value: number) {
    const effectiveMin = determineMin(value);
    setCurrentMin(effectiveMin);
  }
  function updateMaxValue(value: number) {
    const effectiveMax = determineMax(value);
    setCurrentMax(effectiveMax);
  }

  // @ts-ignore
  return <div className={className} data-testid="range-slider">
      <Slider range={true} allowCross={false} min={min} max={max} step={step} defaultValue={[currentMin, currentMax]} value={[currentMin, currentMax]} dots={true} disabled={disabled}
    // @ts-ignore
    handleRender={handleRender} onChange={onChangeSlider} onChangeComplete={value => handleChange && handleChange(value)} />
      <div className="flex justify-between mt-8 gap-2">
        <InputField label={dict.formFieldLabels.rangeFilter.min} name="min" min={min} max={currentMax} step={step} type="number" value={currentMin} disabled={disabled} onKeyUp={onKeyUpInput} onBlur={onBlurInput} className="w-32" />
        <InputField label={dict.formFieldLabels.rangeFilter.max} name="max" min={currentMin} max={max} step={step} type="number" value={currentMax} disabled={disabled} onKeyUp={onKeyUpInput} onBlur={onBlurInput} className="w-32" />
      </div>
    </div>;
}